import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { ShoppingBag, Repeat, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CADENCES = [
  { v: "weekly", l: "Weekly" },
  { v: "monthly", l: "Monthly" },
  { v: "2m", l: "Every 2 months" },
  { v: "3m", l: "Every 3 months" },
  { v: "4m", l: "Every 4 months" },
  { v: "5m", l: "Every 5 months" },
  { v: "6m", l: "Every 6 months" },
  { v: "yearly", l: "Yearly" },
];

export default function ProductCard({ p }) {
  const { add } = useCart();
  const nav = useNavigate();
  const [subOpen, setSubOpen] = useState(false);
  const [cadence, setCadence] = useState("monthly");

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    add(p, { quantity: 1, subscription: null });
    toast.success(`${p.name} added to bag`);
  };
  const shopNow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    add(p, { quantity: 1, subscription: null });
    nav("/checkout");
  };
  const subscribe = (e) => {
    e.preventDefault();
    e.stopPropagation();
    add(p, { quantity: 1, subscription: cadence });
    toast.success(`Subscribed — ${p.name} · ${cadence}`);
    setSubOpen(false);
  };

  const subPrice = Math.round(p.price * (1 - (p.subscription_discount_pct || 10) / 100));

  return (
    <div className="group block" data-testid={`product-card-${p.slug}`}>
      <Link to={`/product/${p.slug}`} className="block">
        <div className="relative overflow-hidden bg-cream-200 aspect-[4/5]">
          <img
            src={p.hero_image}
            alt={p.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute top-3 left-3 text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.22em] uppercase text-ink-900 bg-white/90 px-2 py-1 backdrop-blur">
            {p.category}
          </div>
          {p.compare_at_price && (
            <div className="absolute bottom-3 right-3 text-[9px] sm:text-[10px] tracking-[0.18em] sm:tracking-[0.22em] uppercase bg-ink-900 text-white px-2 py-1">
              Save ₹{Math.round(p.compare_at_price - p.price)}
            </div>
          )}
        </div>
        <div className="pt-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-display text-[18px] leading-tight text-ink-900 truncate">{p.name}</div>
            <div className="text-xs text-ink-500 mt-1 truncate">{p.subtitle} · {p.size}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm tabular-nums text-ink-900">₹{(p.price ?? 0).toFixed(0)}</div>
            {p.compare_at_price ? (
              <div className="text-[11px] tabular-nums text-ink-300 line-through">₹{p.compare_at_price.toFixed(0)}</div>
            ) : null}
          </div>
        </div>
      </Link>

      {/* Quick actions */}
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
        <button
          onClick={quickAdd}
          className="btn-gold-outline py-2.5 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1"
          data-testid={`quick-add-${p.slug}`}
        >
          <ShoppingBag size={12} /> Add
        </button>
        <button
          onClick={shopNow}
          className="btn-primary-ink py-2.5 text-[10px] uppercase tracking-widest"
          data-testid={`shop-now-${p.slug}`}
        >
          Shop now
        </button>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSubOpen((v) => !v); }}
          className="col-span-2 sm:col-span-1 border border-gold-500 text-gold-600 py-2.5 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1 hover:bg-gold-500 hover:text-white transition-colors"
          data-testid={`subscribe-toggle-${p.slug}`}
        >
          <Repeat size={12} /> Subscribe
        </button>
      </div>

      {/* Subscription tray */}
      {subOpen && (
        <div className="mt-2 border hairline p-3 bg-white" data-testid={`subscribe-tray-${p.slug}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs text-ink-700">Save {p.subscription_discount_pct || 10}% · <span className="tabular-nums">₹{subPrice}</span></div>
            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSubOpen(false); }} className="text-ink-500 text-xs">×</button>
          </div>
          <Select value={cadence} onValueChange={setCadence}>
            <SelectTrigger className="rounded-none h-9 text-xs bg-white" data-testid={`cadence-trigger-${p.slug}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none bg-white">
              {CADENCES.map((c) => (
                <SelectItem key={c.v} value={c.v} data-testid={`card-cadence-${p.slug}-${c.v}`}>{c.l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={subscribe}
            className="mt-2 w-full btn-primary-ink py-2 text-[10px] uppercase tracking-widest flex items-center justify-center gap-1"
            data-testid={`confirm-subscribe-${p.slug}`}
          >
            <Check size={12} /> Confirm & subscribe
          </button>
        </div>
      )}
    </div>
  );
}
